import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { ArrowBackIosNew } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import Footer from '../../molecule/footer/footer';
import ProductShowcase from '../../molecule/product/product';
import Collection from '../../molecule/collection/collection';
import Founder from '../../molecule/founder/founder';
import { Shimmer } from '../../atome/motion/motion.styled';
import { ErrorPanel, RetryButton, StateSection } from '../home/home.styled';
import { RootState } from '../../../redux';
import { setProduct } from '../../../redux/product';
import { ProductServices } from '../../../api/product';
import { ProductModel } from '../../models/product/product';
import { color, duration, ease, font } from '../../../theme/tokens';

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.6rem 0 -0.6rem;
  padding: 0.4rem 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.84rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${color.inkSoft};
  transition: color ${duration.fast} ${ease.out}, transform ${duration.fast} ${ease.out};

  svg {
    font-size: 0.85rem;
    transition: transform ${duration.fast} ${ease.out};
  }

  &:hover {
    color: ${color.forest};
  }

  &:hover svg {
    transform: translateX(-4px);
  }
`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items } = useSelector((state: RootState) => state.product);

  const productId = Number(id);
  const fromStore = useMemo(
    () => items.find((item) => item.id === productId) || null,
    [items, productId]
  );

  const [product, setLocalProduct] = useState<ProductModel | null>(fromStore);
  const [loading, setLoading] = useState(!fromStore);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!Number.isFinite(productId)) {
      setError('That product link is not valid.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await ProductServices.getProductById(productId);
      setLocalProduct(data);
      dispatch(setProduct(data));
    } catch (err: any) {
      setError(err?.message || 'We could not load this product.');
    } finally {
      setLoading(false);
    }
  }, [productId, dispatch]);

  useEffect(() => {
    if (fromStore) {
      setLocalProduct(fromStore);
      dispatch(setProduct(fromStore));
      setLoading(false);
      return;
    }
    load();
  }, [fromStore, load, dispatch]);

  const related = useMemo(
    () => items.filter((item) => item.id !== productId).slice(0, 3),
    [items, productId]
  );

  if (loading) {
    return (
      <StateSection>
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <Shimmer $height="460px" $radius="24px" />
            <Box sx={{ display: 'grid', gap: 2, alignContent: 'start', pt: 2 }}>
              <Shimmer $height="18px" />
              <Shimmer $height="44px" />
              <Shimmer $height="120px" $radius="16px" />
              <Shimmer $height="52px" $radius="999px" />
            </Box>
          </Box>
        </Container>
      </StateSection>
    );
  }

  if (error || !product) {
    return (
      <>
        <StateSection>
          <Container maxWidth="lg">
            <ErrorPanel>
              <h3>Product not found</h3>
              <p>{error || 'This product may have been removed from the shop.'}</p>
              <RetryButton onClick={() => navigate('/')}>Back to the shop</RetryButton>
            </ErrorPanel>
          </Container>
        </StateSection>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Container maxWidth="lg">
        <BackLink onClick={() => navigate('/')}>
          <ArrowBackIosNew /> Back to the shop
        </BackLink>
      </Container>

      <ProductShowcase product={product} variant="detail" />

      {related.length > 0 && (
        <Collection
          products={related}
          eyebrow="Keep looking"
          heading="You may also like"
          blurb="Other pieces from the Klarity shelf."
          showFilters={false}
        />
      )}

      <Founder />
      <Footer />
    </>
  );
};

export default ProductDetail;
